import { useState } from 'react';
import { User as UserIcon, Building2, Shield } from 'lucide-react';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { User } from '@/shared/types/entities';
import { PageContainer } from '@/shared/components/layout/PageContainer';
import { AppBadge } from '@/shared/components/ui/AppBadge';
import styles from './SettingsPage.module.scss';

type SettingsTab = 'profile' | 'organization' | 'security';

const TABS: { id: SettingsTab; label: string; icon: typeof UserIcon }[] = [
  { id: 'profile', label: 'Profile', icon: UserIcon },
  { id: 'organization', label: 'Organization', icon: Building2 },
  { id: 'security', label: 'Security', icon: Shield },
];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const user = useAuthStore((s) => s.user);
  const organizations = useAuthStore((s) => s.organizations);
  const orgId = useAuthStore((s) => s.orgId);

  const currentOrg = organizations.find((o) => o.id === orgId);

  return (
    <PageContainer
      title="Settings"
      subtitle="Manage your account and preferences"
      breadcrumbs={[{ label: 'Promote', href: '/promote' }, { label: 'Settings' }]}
    >
      <div className={styles.layout}>
        <nav className={styles.tabs} role="tablist">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={activeTab === tab.id ? styles.tabActive : styles.tab}
                onClick={() => setActiveTab(tab.id)}
                role="tab"
                aria-selected={activeTab === tab.id}
                type="button"
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>

        <div className={styles.panel} role="tabpanel">
          {activeTab === 'profile' && <ProfileSection user={user} />}
          {activeTab === 'organization' && <OrganizationSection orgName={currentOrg?.name} />}
          {activeTab === 'security' && <SecuritySection user={user} />}
        </div>
      </div>
    </PageContainer>
  );
}

function ProfileSection({ user }: { user: User | null }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Profile Information</h2>
      <p className={styles.sectionDescription}>
        Your personal details. Contact your organization admin to update these.
      </p>

      <div className={styles.card}>
        <div className={styles.avatarRow}>
          <div className={styles.avatarLarge}>
            {user?.firstName?.charAt(0)}
            {user?.lastName?.charAt(0)}
          </div>
          <div className={styles.avatarInfo}>
            <span className={styles.avatarName}>
              {user?.firstName} {user?.lastName}
            </span>
            <span className={styles.avatarEmail}>{user?.email}</span>
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>First Name</label>
            <div className={styles.fieldValue}>{user?.firstName ?? '-'}</div>
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Last Name</label>
            <div className={styles.fieldValue}>{user?.lastName ?? '-'}</div>
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Email</label>
            <div className={styles.fieldValue}>{user?.email ?? '-'}</div>
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Role</label>
            <div className={styles.fieldValue}>
              <AppBadge variant="info">{user?.role ?? '-'}</AppBadge>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function OrganizationSection({ orgName }: { orgName?: string }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Organization</h2>
      <p className={styles.sectionDescription}>Details about your current organization.</p>

      <div className={styles.card}>
        <div className={styles.fieldGroup}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Organization Name</label>
            <div className={styles.fieldValue}>{orgName ?? '-'}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SecuritySection({ user }: { user: User | null }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Security</h2>
      <p className={styles.sectionDescription}>Manage your account security settings.</p>

      <div className={styles.card}>
        <div className={styles.fieldGroup}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Login Method</label>
            <div className={styles.fieldValue}>
              <AppBadge variant="success">Events.com SSO</AppBadge>
            </div>
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Account Created</label>
            <div className={styles.fieldValue}>
              {user?.createdAt
                ? new Date(user.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : '-'}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
